import React, { useEffect, useRef } from 'react';
import { offset } from '@floating-ui/core';
import {
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useClick,
  useDismiss,
  flip,
  arrow as arrowMiddleware,
  FloatingArrow,
  FloatingNode,
  FloatingTree,
  useFloatingNodeId,
  useFloatingParentNodeId,
  autoUpdate,
} from '@floating-ui/react';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import Button from '../../Controls/Button/index.tsx';
import Icons from '../../Elements/Icons/index.tsx';
import { ANIMATION_DURATION_MS } from './constants.ts';
import type { IItem } from './Item.tsx';
import Item from './Item.tsx';
import { ButtonComponent, ButtonWrapper, MeasureProbe, SubmenuTrigger, Wrapper } from './styled.ts';
import { TheadButton } from './TheadButton.tsx';

export type DropdownListFC<P = Record<string, never>> = React.FC<P> & {
  Item: React.FC<IItem>;
};

export interface IDropdownList extends React.PropsWithChildren {
  id?: string;
  className?: string;
  dataTestId?: string;
  variant?: 'create' | 'create-secondary' | 'accept' | 'edit' | 'delete';
  disabled?: boolean;
  buttonComponent?: React.ReactNode;
  inThead?: boolean;
  align?: 'left' | 'right';
  zIndex?: number;
  arrow?: boolean;
  minWidth?: boolean;
  /**
   * If `true`, the list opens as a nested flyout submenu on hover and is positioned to the side of
   * its trigger. The trigger is the provided `buttonComponent` (typically a `DropdownList.Item`).
   * An empty submenu (all items render `null`) hides its trigger.
   */
  isSubmenu?: boolean;
  /**
   * Callback that will be called when the visibility of the menu changes
   */
  onVisibleChange?: (open: boolean) => void;
  /**
   * If `true`, menu will be hidden when its reference was hidden.
   */
  trackHiddenReference?: boolean;
  /**
   * Allows to limit max height of the menu.
   */
  maxHeight?: number;
  /**
   * Allows to set a title for the dropdown list.
   */
  title?: string;
}

function selectPlacement(align: 'left' | 'right', isSubmenu: boolean) {
  if (isSubmenu) {
    return align === 'left' ? 'left-start' : 'right-start';
  }

  switch (align) {
    case 'right':
      return 'bottom-start';

    case 'left':
      return 'bottom-end';

    default:
      return 'bottom-start';
  }
}

const DropdownList: React.FC<IDropdownList> = ({
  id,
  className,
  dataTestId,
  children,
  variant = 'edit',
  disabled = false,
  buttonComponent,
  inThead,
  align = 'left',
  zIndex = 3,
  arrow = true,
  minWidth = true,
  isSubmenu = false,
  onVisibleChange,
  trackHiddenReference,
  maxHeight,
  title,
}) => {
  const theme = useTheme();
  const [isShown, setIsShown] = React.useState<boolean>(false);
  const isChildrenArrayEmpty = Array.isArray(children) ? children.every((c) => !!c === false) : false;
  const arrowRef = useRef(null);
  const nodeId = useFloatingNodeId();

  const { refs, floatingStyles, context, placement } = useFloating({
    nodeId,
    open: isShown,
    placement: selectPlacement(align, isSubmenu),
    whileElementsMounted: autoUpdate,
    onOpenChange: setIsShown,
    middleware: [
      offset(isSubmenu ? 1 : 5),
      flip({
        fallbackPlacements: isSubmenu
          ? ['left-start', 'right-start', 'left-end', 'right-end']
          : ['bottom-end', 'bottom-start', 'top-end', 'top-start'],
      }),
      // The arrow only renders in the non-submenu branch, so skip its middleware for submenus.
      ...(isSubmenu
        ? []
        : [
            // eslint-disable-next-line react-hooks/refs
            arrowMiddleware({
              element: arrowRef,
            }),
          ]),
    ],
  });

  useEffect(() => {
    onVisibleChange?.(isShown);
  }, [isShown, onVisibleChange]);

  const focus = useFocus(context, { enabled: !isSubmenu });
  // Submenus open on hover only: a click-opened submenu would not hover-close, so hovering another
  // section would leave two submenus open at once. Non-submenu behaviour is unchanged.
  const click = useClick(context, { enabled: !isSubmenu });
  const hover = useHover(context, { enabled: isSubmenu && !disabled, delay: { open: 150, close: 100 } });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, focus, hover]);

  useDismiss(context, {
    enabled: isShown,
  });

  const probeRef = useRef<HTMLDivElement | null>(null);
  const [isSubmenuEmpty, setIsSubmenuEmpty] = React.useState<boolean>(false);

  // A submenu whose items all render `null` at runtime must hide its trigger. While the submenu is
  // closed its items live in an off-screen (display:none) probe — mounted only when closed, so the
  // visible flyout never duplicates their data-testids. Observed because the leaf buttons toggle
  // from context (selection) without re-rendering this component.
  React.useLayoutEffect(() => {
    const node = probeRef.current;
    if (!isSubmenu || isShown || !node) {
      return;
    }
    const update = () => setIsSubmenuEmpty(node.childElementCount === 0);
    update();
    const observer = new MutationObserver(update);
    observer.observe(node, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isSubmenu, isShown]);

  const isTop = placement.startsWith('top');

  const stopPropagation = useCallbackRef((evt: React.MouseEvent) => evt.stopPropagation());

  if (isChildrenArrayEmpty) {
    return null;
  }

  return (
    <FloatingNode id={nodeId}>
      {isSubmenu
        ? isShown &&
          ReactDOM.createPortal(
            // Mounted only while open, so it positions against the settled trigger (no first-open
            // jump) and runs no perpetual autoUpdate loop while closed.
            <Wrapper
              ref={refs.setFloating}
              {...getFloatingProps()}
              style={floatingStyles}
              $visible={isShown}
              $isTop={isTop}
              $zIndex={zIndex}
              $minWidth={minWidth}
              $trackHiddenReference={trackHiddenReference}
              $maxHeight={maxHeight}>
              {children}
            </Wrapper>,
            document.body,
          )
        : ReactDOM.createPortal(
            <CSSTransition in={isShown} transitionAppear timeout={ANIMATION_DURATION_MS} unmountOnExit>
              <Wrapper
                // eslint-disable-next-line react-hooks/refs
                ref={refs.setFloating}
                {...getFloatingProps()}
                style={floatingStyles}
                $visible={isShown}
                $isTop={isTop}
                $zIndex={zIndex}
                $minWidth={minWidth}
                $trackHiddenReference={trackHiddenReference}
                $maxHeight={maxHeight}>
                {arrow && <FloatingArrow ref={arrowRef} context={context} fill={theme.colors.grey00_1} width={11} height={5} />}
                {children}
              </Wrapper>
            </CSSTransition>,
            document.body,
          )}
      {isSubmenu && !isShown && (
        <MeasureProbe ref={probeRef} aria-hidden>
          {children}
        </MeasureProbe>
      )}
      <ButtonWrapper onClick={stopPropagation} $block={isSubmenu}>
        {isSubmenu && buttonComponent && !isSubmenuEmpty && (
          <SubmenuTrigger
            id={id}
            // eslint-disable-next-line react-hooks/refs
            ref={refs.setReference}
            {...getReferenceProps()}
            className={className}
            data-testid={dataTestId}>
            {buttonComponent}
          </SubmenuTrigger>
        )}
        {!isSubmenu && inThead && (
          <TheadButton
            id={id}
            // eslint-disable-next-line react-hooks/refs
            ref={refs.setReference}
            {...getReferenceProps()}
            className={className}
            data-testid={dataTestId}
            variant={variant}
            disabled={disabled}
            square
            active={isShown}>
            <Icons.Angle size={16} color={theme.colors.grey00_1} />
          </TheadButton>
        )}
        {!isSubmenu && buttonComponent && !inThead && (
          // eslint-disable-next-line react-hooks/refs
          <ButtonComponent ref={refs.setReference} {...getReferenceProps()}>
            {buttonComponent}
          </ButtonComponent>
        )}
        {!isSubmenu && !buttonComponent && !inThead && (
          <Button
            id={id}
            className={className}
            data-testid={dataTestId}
            variant={variant}
            disabled={disabled}
            square={!title}
            // eslint-disable-next-line react-hooks/refs
            ref={refs.setReference}
            {...getReferenceProps()}>
            {title && <span>{title}</span>}
            {!title && <Icons.Angle size={16} color={theme.colors.grey00_2} direction={isShown ? 'top' : 'bottom'} />}
          </Button>
        )}
      </ButtonWrapper>
    </FloatingNode>
  );
};

const DropdownListMemo = React.memo(DropdownList);

const DropdownListWrapper: DropdownListFC<IDropdownList> = (props) => {
  // Register in a FloatingTree so a submenu's portalled flyout counts as "inside" the parent menu.
  // Without it, a pointerdown on a submenu item reads as an outside press and can dismiss the parent
  // before the item's onClick fires. The root dropdown creates the tree; nested dropdowns join it.
  const parentNodeId = useFloatingParentNodeId();

  if (parentNodeId === null) {
    return (
      <FloatingTree>
        <DropdownListMemo {...props} />
      </FloatingTree>
    );
  }

  return <DropdownListMemo {...props} />;
};

DropdownListWrapper.Item = Item;

export default DropdownListWrapper;
